import Separator from "@/v2-ui/separator"
import Table from "@/v2-backoffice-client/components/table"
import type { UserTenantFragment } from "@/v2-backoffice-client/user/tenant/__types__/user.tenant.fragment"
import UserStatusUpdateManyMenu from "@/v2-backoffice-client/user/status/update/many/UserStatusUpdateManyMenu"
import UserContactSyncManyButton from "@/v2-backoffice-client/user/contact/sync/many/UserContactSyncManyButton"
import UserMigrateManyButton from "@/v2-backoffice-client/user/migration/many/UserMigrateManyButton"
import type { MigrationCenterTenantRef } from "@/v2-backoffice-client/tenant/migration/center/tenant.migration.center.types"
import TenantUserRemoveManyButton from "@/v2-backoffice-client/tenant/user/remove/many/TenantUserRemoveManyButton"
import TenantUserRestoreManyButton from "@/v2-backoffice-client/tenant/user/restore/many/TenantUserRestoreManyButton"
import TenantUserAddManySelector from "@/v2-backoffice-client/tenant/user/add/many/TenantUserAddManySelector"
import UserCustomerUpdateManySelector from "@/v2-backoffice-client/user/customer/update/many/UserCustomerUpdateManySelector"

export type TenantUserTableSelectionToolbarProps = {
  onlyDeleted?: boolean
  uts: UserTenantFragment[]
  /** The panel's team — Fusion sends pin to it, so multi-team members
   *  migrate under THIS team instead of failing as ambiguous. */
  tenant?: MigrationCenterTenantRef
  reset?(): void
  /** Remove/restore change which filter bucket rows belong to — the list
   *  must refetch or they linger in the wrong view (reset only clears the
   *  selection). */
  onMembershipChanged?(): void
}

function TenantUserTableSelectionToolbar(
  props: TenantUserTableSelectionToolbarProps,
) {
  const { onlyDeleted, uts, tenant, reset, onMembershipChanged } = props

  function resetAndRefetch() {
    reset?.()
    onMembershipChanged?.()
  }

  const userTenantIds = uts.map((ut) => ut.Id)
  const users = uts.flatMap((ut) => (ut.User ? [ut.User] : []))

  return (
    <>
      <Table.ExportSelectionButton
        fileName="tenant-users"
        selectionCount={uts.length}
      />

      {onlyDeleted ? (
        <TenantUserRestoreManyButton
          isStacked
          userTenantIds={userTenantIds}
          onCompleted={resetAndRefetch}
        >
          Restore
        </TenantUserRestoreManyButton>
      ) : (
        <TenantUserRemoveManyButton
          isStacked
          userTenantIds={userTenantIds}
          onCompleted={resetAndRefetch}
        >
          Remove
        </TenantUserRemoveManyButton>
      )}

      {/* The same membership actions as the Users and customer boards: add
          the members to another team, or move them to the customer that
          owns them (the fix when a team sat under the wrong customer and
          its members inherited it). */}
      <TenantUserAddManySelector
        isStacked
        variant="ghost"
        users={users}
        onCompleted={reset}
        content="Add the selected members to another team."
      >
        Team
      </TenantUserAddManySelector>

      <UserCustomerUpdateManySelector
        isStacked
        variant="ghost"
        users={users}
        onCompleted={reset}
        content="Update the customer that owns the selected members: whose licenses and seats apply to them."
      >
        Customer
      </UserCustomerUpdateManySelector>

      <UserContactSyncManyButton users={users} onCompleted={reset} />

      {!onlyDeleted && tenant && (
        <UserMigrateManyButton
          users={users}
          tenant={tenant}
          onCompleted={resetAndRefetch}
        />
      )}

      <Separator orientation="vertical" />
      <UserStatusUpdateManyMenu users={users} onCompleted={reset} />
    </>
  )
}

export default TenantUserTableSelectionToolbar
